/**
 * The Tribuna: an octagonal domed crossing on the route. A square footprint
 * with its four corners chamfered off, a clerestory attic over the main order,
 * and a stepped corbelled dome that closes on a glowing lantern — the Uffizi
 * octagon by way of a Roman tomb. Doors arrive on any of the four axis sides;
 * the corner cut shrinks (or vanishes) to keep every jamb on a flat wall run.
 */

import { PALACE } from '../constants';
import { angledSegment } from '../hang';

const SQ2 = Math.SQRT2;
const ORDER_H = 6.6; // string course: top of the main order, foot of the attic
const WIN_Y0 = 7.15; // clerestory band in the attic
const WIN_Y1 = 8.55;
const DOOR_CLEAR = 0.85; // air kept between a door jamb and a chamfer's end
const STEP = 0.62; // corbel: how far each ring steps inward
const RISE = 0.34; // corbel: how far each ring steps up
const BAND_T = 0.95; // corbel ring thickness — wider than STEP, so consecutive
// rings always overlap in plan and the dome reads as one solid surface
const DOME_EYE = 1.5; // stop corbelling once the opening is this close in

// Corners in plan order, with the two sides that meet at each and, for every
// side, which corner sits at its low and high end.
const CORNERS = ['SW', 'SE', 'NE', 'NW'];
const SIDE_ENDS = { S: [0, 1], N: [3, 2], W: [0, 3], E: [1, 2] };

/**
 * The corner cut at each of the four corners. Each starts from the regular
 * octagon's value and shrinks until every door on the two sides meeting there
 * clears the chamfer's end (running at 45°, a chamfer reaches a little past
 * its nominal end once its thickness counts). A corner with no room left
 * stays square, so the doors always land on a flat wall run.
 */
const cornerCuts = (node) => {
  const { WALL_T, DOOR_W } = PALACE;
  const a = Math.min(node.x2 - node.x1, node.z2 - node.z1) / 2 - WALL_T / 2;
  const cuts = CORNERS.map(() => a * (2 - SQ2));
  for (const door of node.doors || []) {
    const half = (door.w || DOOR_W) / 2 + DOOR_CLEAR;
    const horizontal = door.side === 'N' || door.side === 'S';
    const lo = horizontal ? node.x1 : node.z1;
    const hi = horizontal ? node.x2 : node.z2;
    const [atLo, atHi] = SIDE_ENDS[door.side];
    cuts[atLo] = Math.min(cuts[atLo], door.at - half - lo);
    cuts[atHi] = Math.min(cuts[atHi], hi - door.at - half);
  }
  return cuts.map((c) => (c >= 1.2 ? c : 0));
};

// Sutherland–Hodgman: the part of a convex polygon with n·p <= d. Clipping the
// footprint by all eight octagon half-planes, each pushed inward by the same
// offset, gives the corbel ring at that offset — and degenerates gracefully
// (chamfers simply vanish) when the octagon is nearly square.
const clipPoly = (poly, nx, nz, d) => {
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    const dp = nx * p[0] + nz * p[1] - d;
    const dq = nx * q[0] + nz * q[1] - d;
    if (dp <= 0) out.push(p);
    if ((dp < 0 && dq > 0) || (dp > 0 && dq < 0)) {
      const t = dp / (dp - dq);
      out.push([p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t]);
    }
  }
  return out;
};

// A band riding on a chamfer face: the chamfer's centreline pushed `proud`
// past the wall face, thick enough to stay buried in the wall behind it.
const chamferBand = (T, ch, y0, y1, proud) => {
  const t = 0.125 + proud;
  const off = 0.1625 + proud / 2;
  return T.angledWall(
    ch.ax + ch.nx * off, ch.az + ch.nz * off,
    ch.bx + ch.nx * off, ch.bz + ch.nz * off,
    y1, t, y0
  ).geo;
};

/**
 * The chamfer faces this room ends up with, in plan order. Shared by segments()
 * and build() so a hero can only ever hang on a chamfer that actually gets
 * built. `nx/nz` is the inward normal; `plane` and `lid` are for the dome.
 */
const chamfersOf = (node) => {
  const { WALL_T } = PALACE;
  const mx1 = node.x1 + WALL_T / 2;
  const mx2 = node.x2 - WALL_T / 2;
  const mz1 = node.z1 + WALL_T / 2;
  const mz2 = node.z2 - WALL_T / 2;
  const corner = (i, c) => [
    { ax: mx1 + c, az: mz1, bx: mx1, bz: mz1 + c, nx: 1 / SQ2, nz: 1 / SQ2, c,
      plane: { nx: -1 / SQ2, nz: -1 / SQ2, d: (-(mx1 + c) - mz1) / SQ2 },
      lid: [node.x1, mx1 + c, node.z1, mz1 + c] },
    { ax: mx2 - c, az: mz1, bx: mx2, bz: mz1 + c, nx: -1 / SQ2, nz: 1 / SQ2, c,
      plane: { nx: 1 / SQ2, nz: -1 / SQ2, d: ((mx2 - c) - mz1) / SQ2 },
      lid: [mx2 - c, node.x2, node.z1, mz1 + c] },
    { ax: mx2 - c, az: mz2, bx: mx2, bz: mz2 - c, nx: -1 / SQ2, nz: -1 / SQ2, c,
      plane: { nx: 1 / SQ2, nz: 1 / SQ2, d: ((mx2 - c) + mz2) / SQ2 },
      lid: [mx2 - c, node.x2, mz2 - c, node.z2] },
    { ax: mx1 + c, az: mz2, bx: mx1, bz: mz2 - c, nx: 1 / SQ2, nz: -1 / SQ2, c,
      plane: { nx: -1 / SQ2, nz: 1 / SQ2, d: (-(mx1 + c) + mz2) / SQ2 },
      lid: [node.x1, mx1 + c, mz2 - c, node.z2] },
  ][i];
  const cuts = cornerCuts(node);
  return { cuts, chamfers: cuts.map((c, i) => (c > 0 ? corner(i, c) : null)).filter(Boolean) };
};

const HERO_INSET = 0.7; // air left at each end of a chamfer, off the corner joint

const tribuna = {
  id: 'tribuna',
  role: 'route',
  weight: 2,

  plan(rand) {
    const size = 11 + rand() * 2;
    return { w: size, d: size, height: 9.2 + rand() * 0.6 };
  },

  // The four axis walls carry the doors; the chamfers are what's left, and a
  // chamfer facing you across an octagon wants exactly one picture on it.
  segments(node) {
    return chamfersOf(node).chamfers.map((ch) => {
      const len = Math.hypot(ch.bx - ch.ax, ch.bz - ch.az);
      const t = HERO_INSET / len;
      return angledSegment(
        ch.ax + (ch.bx - ch.ax) * t, ch.az + (ch.bz - ch.az) * t,
        ch.bx - (ch.bx - ch.ax) * t, ch.bz - (ch.bz - ch.az) * t,
        ch.nx, ch.nz,
        { style: 'hero' }
      );
    });
  },

  build(node, T) {
    const { WALL_T } = T.PALACE;
    const M = T.kit.materials;
    const height = node.height;
    const group = new T.THREE.Group();
    const colliders = [];

    const cx = (node.x1 + node.x2) / 2;
    const cz = (node.z1 + node.z2) / 2;
    const mx1 = node.x1 + WALL_T / 2; // wall centrelines: where the chamfers
    const mx2 = node.x2 - WALL_T / 2; // meet the perimeter walls
    const mz1 = node.z1 + WALL_T / 2;
    const mz2 = node.z2 - WALL_T / 2;
    const xI1 = node.x1 + WALL_T;
    const xI2 = node.x2 - WALL_T;
    const zI1 = node.z1 + WALL_T;
    const zI2 = node.z2 - WALL_T;
    const { cuts, chamfers } = chamfersOf(node);

    // --- the octagon: four perimeter walls with their doors cut, plus a
    // chamfer closing off each corner that has the room for one.
    const wallGeos = [];
    for (const side of ['N', 'S', 'E', 'W']) {
      const w = T.wallForSide(node, side, height);
      wallGeos.push(...w.geos);
      colliders.push(...w.colliders);
    }
    for (const ch of chamfers) {
      const w = T.angledWall(ch.ax, ch.az, ch.bx, ch.bz, height);
      wallGeos.push(w.geo);
      colliders.push(w.collider);
    }
    group.add(T.mergedMesh(wallGeos, T.kit.wallFor(node.palette || 0, true)));

    // Floor, and lids over the four dead corner wells behind the chamfers so
    // the dome never looks out into them.
    group.add(T.mergedMesh(
      [T.box(node.x1, node.x2, -0.25, 0, node.z1, node.z2)],
      M.marble
    ));
    if (chamfers.length) {
      group.add(T.mergedMesh(
        chamfers.map((ch) => T.box(ch.lid[0], ch.lid[1], height, height + 0.22, ch.lid[2], ch.lid[3])),
        M.ceiling
      ));
    }

    // --- trim: baseboards and the string course at the top of the main order,
    // then a second cornice at the attic head where the dome springs.
    const trim = T.baseAndCornice(node, ORDER_H, 0.34);
    const cornice = (y0, y1, proud) => {
      trim.push(
        T.box(xI1, xI2, y0, y1, zI2 - proud, zI2),
        T.box(xI1, xI2, y0, y1, zI1, zI1 + proud),
        T.box(xI2 - proud, xI2, y0, y1, zI1 + proud, zI2 - proud),
        T.box(xI1, xI1 + proud, y0, y1, zI1 + proud, zI2 - proud)
      );
      for (const ch of chamfers) trim.push(chamferBand(T, ch, y0, y1, proud));
    };
    for (const ch of chamfers) trim.push(chamferBand(T, ch, ORDER_H - 0.34, ORDER_H, 0.065));
    cornice(height - 0.42, height, 0.09);
    group.add(T.mergedMesh(trim, M.trim));

    // --- clerestory: one lit panel per octagon face, up in the attic.
    const win = [];
    for (const side of ['N', 'S', 'E', 'W']) {
      const horizontal = side === 'N' || side === 'S';
      const [lo, hi] = SIDE_ENDS[side];
      // the flat run left between this side's two corners, whatever each was cut to
      const runLo = (horizontal ? mx1 : mz1) + cuts[lo];
      const runHi = (horizontal ? mx2 : mz2) - cuts[hi];
      const mid = horizontal ? cx : cz;
      const ww = Math.min(2.6, 2 * Math.min(mid - runLo, runHi - mid) - 0.7);
      if (ww < 0.6) continue;
      const a = mid - ww / 2;
      const b = mid + ww / 2;
      if (side === 'N') win.push(T.box(a, b, WIN_Y0, WIN_Y1, zI2 - 0.04, zI2 + 0.02));
      else if (side === 'S') win.push(T.box(a, b, WIN_Y0, WIN_Y1, zI1 - 0.02, zI1 + 0.04));
      else if (side === 'E') win.push(T.box(xI2 - 0.04, xI2 + 0.02, WIN_Y0, WIN_Y1, a, b));
      else win.push(T.box(xI1 - 0.02, xI1 + 0.04, WIN_Y0, WIN_Y1, a, b));
    }
    for (const ch of chamfers) {
      const cw = Math.min(2.2, ch.c * SQ2 * 0.55);
      if (cw < 0.6) continue;
      const len = Math.hypot(ch.bx - ch.ax, ch.bz - ch.az);
      const ux = (ch.bx - ch.ax) / len;
      const uz = (ch.bz - ch.az) / len;
      const hx = (ch.ax + ch.bx) / 2;
      const hz = (ch.az + ch.bz) / 2;
      win.push(T.angledWall(
        hx - ux * cw / 2 + ch.nx * 0.235, hz - uz * cw / 2 + ch.nz * 0.235,
        hx + ux * cw / 2 + ch.nx * 0.235, hz + uz * cw / 2 + ch.nz * 0.235,
        WIN_Y1, 0.06, WIN_Y0
      ).geo);
    }
    if (win.length) group.add(T.mergedMesh(win, M.laylight));

    // --- the dome: rings of the octagon, each offset one STEP further in and
    // one RISE higher, corbelling over until the opening is small enough to
    // cap with the lantern.
    const planes = [
      { nx: 1, nz: 0, d: mx2 },
      { nx: -1, nz: 0, d: -mx1 },
      { nx: 0, nz: 1, d: mz2 },
      { nx: 0, nz: -1, d: -mz1 },
    ];
    for (const ch of chamfers) planes.push(ch.plane);
    const inr = Math.min(...planes.map((p) => p.d - p.nx * cx - p.nz * cz));
    // ... corbelling until the opening is small, or until the apex reaches the
    // palace's 12m ceiling — a wider eye simply takes a wider lantern.
    const rings = Math.max(2, Math.min(
      Math.floor((inr - DOME_EYE) / STEP),
      Math.floor((11.9 - height) / RISE)
    ));
    const domeGeos = [];
    let eye = null; // the opening the topmost ring leaves, for the lantern
    for (let k = 1; k <= rings; k++) {
      let poly = [
        [node.x1 - 1, node.z1 - 1], [node.x2 + 1, node.z1 - 1],
        [node.x2 + 1, node.z2 + 1], [node.x1 - 1, node.z2 + 1],
      ];
      for (const p of planes) poly = clipPoly(poly, p.nx, p.nz, p.d - k * STEP);
      if (poly.length < 3) break;
      eye = poly;
      const y0 = height + (k - 1) * RISE;
      for (let i = 0; i < poly.length; i++) {
        const p = poly[i];
        const q = poly[(i + 1) % poly.length];
        if (Math.hypot(q[0] - p[0], q[1] - p[1]) < 0.08) continue;
        domeGeos.push(T.angledWall(p[0], p[1], q[0], q[1], y0 + RISE, BAND_T, y0).geo);
      }
    }
    if (domeGeos.length) group.add(T.mergedMesh(domeGeos, M.dome));

    // The lantern: an emissive plate buried in the topmost ring, so the glow
    // shows exactly as the opening the corbel left.
    const yTop = height + rings * RISE;
    const ex = eye ? eye.reduce((s, p) => s + p[0], 0) / eye.length : cx;
    const ez = eye ? eye.reduce((s, p) => s + p[1], 0) / eye.length : cz;
    const eyeR = eye
      ? Math.max(...eye.map((p) => Math.hypot(p[0] - ex, p[1] - ez)))
      : inr - rings * STEP;
    const lantern = new T.THREE.Mesh(
      new T.THREE.CircleGeometry(Math.max(0.4, eyeR - BAND_T / 2 + 0.25), 24),
      M.laylight
    );
    lantern.rotation.x = Math.PI / 2;
    lantern.position.set(ex, yTop - 0.12, ez);
    lantern.matrixAutoUpdate = false;
    lantern.updateMatrix();
    group.add(lantern);

    const light = new T.THREE.PointLight(0xfff0d8, 26, 24, 1.8);
    light.position.set(cx, ORDER_H + 0.8, cz);
    group.add(light);

    // An octagonal roundel inlaid in the marble, echoing the plan.
    const roundel = new T.THREE.Mesh(
      new T.THREE.CircleGeometry(inr * 0.5, 8, Math.PI / 8),
      M.stone || M.marble
    );
    roundel.rotation.x = -Math.PI / 2;
    roundel.position.set(cx, 0.014, cz);
    roundel.matrixAutoUpdate = false;
    roundel.updateMatrix();
    group.add(roundel);

    const casings = T.casingsForNode(node);
    if (casings.length) group.add(T.mergedMesh(casings, M.marble));

    return { group, colliders };
  },
};

export default tribuna;
